import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API, SHOW_PRICING } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { CreditCard, Loader2, Share2, Lock, Instagram, Linkedin } from 'lucide-react';

const VisualCard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [card, setCard] = useState(null);

  const tier = user?.subscription_tier || 'free';
  const subscriptionStatus = user?.subscription_status || 'inactive';
  const isBetaUser = user?.is_beta_user || user?.beta_access || subscriptionStatus === 'beta';
  const hasAccess = !SHOW_PRICING || tier === 'pro' || tier === 'experience' || isBetaUser;

  useEffect(() => {
    if (hasAccess) fetchLatest();
  }, [hasAccess]);

  const fetchLatest = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/visual-card/latest`, { headers });
      if (response.data.card) setCard(response.data.card);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/visual-card/generate`, {}, { headers });
      setCard(response.data);
      toast.success('Visual Card created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = (platform) => {
    const text = `${card.brand_name} - ${card.brand_tagline}. ${card.signature_message}`;
    const url = window.location.origin;
    
    if (platform === 'instagram') {
      navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard! Open Instagram to share.');
    } else if (platform === 'linkedin') {
      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&summary=${encodeURIComponent(text)}`, '_blank');
    }
  };

  if (!hasAccess) {
    return (
      <DashboardLayout title="Visual Card Premium">
        <div className="max-w-2xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-premium p-12 text-center">
            <Lock className="text-[#D4AF37] mx-auto mb-6" size={64} />
            <h2 className="font-serif text-2xl text-white mb-4">Pro Feature</h2>
            <p className="text-[#A1A1AA] mb-8 max-w-md mx-auto">
              Visual Card Premium is exclusive to Pro subscribers. Create shareable brand identity cards.
            </p>
            <Button onClick={() => navigate('/dashboard')} className="btn-gold px-8 py-4" data-testid="upgrade-btn">
              Upgrade to Pro
            </Button>
          </motion.div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Visual Card Premium">
      <div className="max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-premium p-8 mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <CreditCard className="text-[#E5C585] mb-4" size={28} />
              <h2 className="font-serif text-2xl text-white mb-2">Create Visual Card</h2>
              <p className="text-[#A1A1AA]">Generate a shareable brand identity card.</p>
            </div>
            <Button onClick={handleGenerate} disabled={loading} className="btn-gold px-8 py-4 flex items-center gap-2" data-testid="generate-card-btn">
              {loading ? (<><Loader2 className="animate-spin" size={18} />Creating...</>) : card ? 'Regenerate' : 'Create Card'}
            </Button>
          </div>
        </motion.div>

        {card && (
          <div className="space-y-6">
            {/* Card Preview */}
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1 }} className="relative">
              <div 
                className="aspect-[4/3] p-12 flex flex-col justify-between"
                style={{ 
                  background: `linear-gradient(135deg, ${card.brand_colors?.[0] || '#050505'} 0%, ${card.brand_colors?.[1] || '#0A0A0A'} 100%)`,
                  border: `2px solid ${card.brand_colors?.[2] || '#D4AF37'}`
                }}
              >
                <div>
                  <span 
                    className="text-xs font-accent tracking-[0.3em]"
                    style={{ color: card.brand_colors?.[2] || '#E5C585' }}
                  >
                    {card.brand_energy}
                  </span>
                </div>
                
                <div>
                  <h2 className="font-serif text-4xl md:text-5xl text-white mb-4">{card.brand_name}</h2>
                  <p className="text-xl text-white/80 mb-6">{card.brand_tagline}</p>
                  <p className="text-white/60 max-w-md">{card.signature_message}</p>
                </div>
                
                <div className="flex items-center gap-6">
                  <div className="flex gap-2">
                    {card.brand_colors?.map((color, i) => (
                      <div key={i} className="w-6 h-6 rounded-full border border-white/20" style={{ backgroundColor: color }} />
                    ))}
                  </div>
                  <div className="text-white/60 text-sm">
                    {card.social_handles?.instagram} | {card.social_handles?.linkedin}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Share Buttons */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="flex gap-4 justify-center">
              <Button onClick={() => handleShare('instagram')} className="btn-outline-gold px-6 py-4 flex items-center gap-2" data-testid="share-instagram">
                <Instagram size={18} />
                Share on Instagram
              </Button>
              <Button onClick={() => handleShare('linkedin')} className="btn-outline-gold px-6 py-4 flex items-center gap-2" data-testid="share-linkedin">
                <Linkedin size={18} />
                Share on LinkedIn
              </Button>
            </motion.div>
          </div>
        )}

        {!card && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-premium p-16 text-center">
            <CreditCard className="text-[#27272A] mx-auto mb-6" size={80} />
            <h3 className="font-serif text-2xl text-white mb-4">Create Your Visual Card</h3>
            <p className="text-[#A1A1AA] max-w-md mx-auto">Generate a beautiful brand identity card to share on social media.</p>
          </motion.div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default VisualCard;
